/**
 * A sheet's writes of its rows' own fields (`SheetRowFields`): the row-field
 * ledger the sheet's rows enter (`RowWrites` — the client's, or the sheet's
 * own over its own copy), the sheet's copy of the rows in that ledger (`peek`
 * / `show`: the metadata record, an alias's instance props, a formula
 * rebind), and the writes the sheet makes through it — a display-attribute
 * write (a row's own bag, a child slot, a renderer's copy-on-write), a local
 * attribute patch, the store-held wire keys a structural op changed, and the
 * one transport (`Backend.updateRow`).
 *
 * It owns the ledger seat and the write rules; the rows, stores and backend
 * stay the sheet's, handed in through three narrow roles
 * (`SheetRowFields.Rows`, `.Stores`, `.Persist`) the sheet builds over its
 * private members. It does not name `Sheet`, so it sits below it.
 */

import type { Option } from "../core/option.ts";
import { Json, type JsonObject } from "../core/json.ts";
import { SdkRequestError } from "../core/errors.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { RowWrites } from "../ledger/row-writes.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { AliasRow } from "../rows/variants/alias-row.ts";
import { SourceRow } from "../rows/variants/source-row.ts";
import { RowMetadata, RowMetadataStore } from "../stores/index.ts";
import type { InvalidationSink } from "../tx/invalidation.ts";
import type { RawRow } from "../wire/raw-sheet-wire.ts";
import type { SheetStores } from "./rows-sheet.ts";
import { attrContext, SheetRendererCow } from "./sheet-renderer-cow.ts";

/** Types that go with `SheetRowFields`: its three host roles. */
export namespace SheetRowFields {
  /** Which rows the sheet holds. */
  export interface Rows {
    /** The row `id` names, or `null` when it is not this sheet's. */
    row(id: RowLocalId): Option<ModelRow>;
    /** The sheet's route key, named in an unaddressable row's error. */
    networkKey(): string;
  }

  /** The stores the sheet's copy of a row is painted in. */
  export interface Stores {
    stores(): Pick<SheetStores, "rowMetadata" | "aliasInstance" | "renderless">;
    /** `row` as its wire row (`snapshotRow` over the sheet's stores). */
    snapshot(row: ModelRow): RawRow;
    /** The row's formula changed in the sheet's copy: rebind it and recompute it with its dependents. */
    formulaChanged(rowId: RowLocalId, row: ModelRow): void;
  }

  /** Where the sheet's row writes go. */
  export interface Persist {
    /** Whether the sheet is ephemeral (nothing is sent until it is promoted). */
    isEphemeral(): boolean;
    /** Send a row PATCH of row `rowId` (`Backend.updateRow` of the row as it is keyed when sent). */
    readonly transport: (rowId: RowLocalId) => RowWrites.Transport;
    /** A renderer target's write: the source's default renderer, materialized, takes `patch` on its own bag. */
    materializeAndPatchRenderer(sourceId: RowLocalId, patch: SheetRendererCow.Patch): Promise<SheetRendererCow.AttrWrite>;
  }
}

/** A sheet's row-field writes (see the module note). */
export class SheetRowFields {
  /**
   * The keys a row's wire bag takes from the sheet's other stores rather than
   * its metadata (`snapshotRow`): an alias's instance state, a source's
   * render-less flag, a renderer's id.
   */
  static readonly #WIRE_KEYS: readonly string[] = ["template", "props", "overrides", "hydrated", "renderless", "rendererId"];

  readonly #rows: SheetRowFields.Rows;
  readonly #stores: SheetRowFields.Stores;
  readonly #persist: SheetRowFields.Persist;
  /**
   * The row-field ledger every write of the sheet's rows' own fields enters
   * (`RowWrites`): the client's (`SheetModelCache.rowWrites`), else the
   * sheet's own over its own copy (a standalone sheet).
   */
  readonly ledger: RowWrites;

  /** `ledger`: the client's; `null` builds the sheet's own over this copy, announcing on `sink`. */
  constructor(
    rows: SheetRowFields.Rows,
    stores: SheetRowFields.Stores,
    persist: SheetRowFields.Persist,
    ledger: Option<RowWrites>,
    sink: InvalidationSink,
  ) {
    this.#rows = rows;
    this.#stores = stores;
    this.#persist = persist;
    if (ledger != null) {
      this.ledger = ledger;
    } else {
      // A standalone sheet: its own ledger, over its own copy of its rows.
      const own = new RowWrites(() => [sink]);
      own.addDisplay({ peek: (row) => this.peek(row), show: (row, delta) => this.show(row, delta) }, "first");
      this.ledger = own;
    }
  }

  /** How the sheet enters a row write: announced (and taken back) as `RowMetadataStore.ACTOR`. */
  static writeOpts(): RowWrites.Begin {
    return { correction: RowMetadataStore.ACTOR, quietAttributes: false, capture: null };
  }

  /**
   * Unified optimistic-local-first display-attr write — the single seam
   * the envelope option write (`RowEnvelopeDrive.writeTarget`) routes EVERY
   * `*-attr` target through. The local
   * rows-side patch (and any renderer materialize) happens synchronously so
   * the grid repaints immediately; the returned promise carries the persist
   * verdict (`Sheet.AttrWrite`): it rejects with the backend's error when the
   * backend refuses, and an ephemeral (not-yet-promoted) sheet resolves
   * `{ persisted: "deferred-to-promote" }` — its value round-trips through
   * serialize → hydrate on promote.
   *
   * Renderer targets are copy-on-write: when `rowId` names a renderer child of
   * a source (possibly still the synthetic default renderer), the renderer is
   * materialized to a real row and `patch` lands on its OWN bag — never the
   * source's — via `setRendererDisplayAttr`. All other targets patch the named
   * row's own bag. `patch` may carry multiple keys (e.g. a two-handle hour
   * range writing `startHr` + `endHr` in one write).
   */
  async setDisplayAttr(rowId: RowLocalId, patch: SheetRendererCow.Patch): Promise<SheetRendererCow.AttrWrite> {
    const row = this.#rows.row(rowId);
    if (row != null && row.descriptor.role === "renderer" && row.parent instanceof SourceRow) {
      return await this.#persist.materializeAndPatchRenderer(row.parent.id, patch);
    }
    // Direct row attr (alias / layout / source) — paint, then persist.
    this.requireRow(rowId, "set-display-attr");
    return await this.write(
      rowId,
      RowWrites.mergeAttributes("shallow", patch, []),
      attrContext(rowId, Object.keys(patch)),
    );
  }

  /**
   * Optimistic-local-first write for a per-CHILD override slot
   * (the `children[preset]` entry of the row's attributes). The counterpart of
   * {@link setDisplayAttr} for a virtual child whose config persists on its
   * host (e.g. calendar Hours/type/timezone living under the alias's
   * `children["calendar-body"]` slot). Reads the current `children` map, merges
   * `patch` into the named slot (preserving sibling slots and the slot's other
   * keys — e.g. a `visible` flag), then patches + persists with the same
   * verdict as `setDisplayAttr`. In the transitional app the durable persist is the
   * options app-mirror (`composed-row-options.ts`); this keeps the rows-side
   * store live for immediate reads and persists for headless callers.
   */
  async setChildDisplayAttrs(
    rowId: RowLocalId,
    preset: string,
    patch: SheetRendererCow.Patch,
  ): Promise<SheetRendererCow.AttrWrite> {
    this.requireRow(rowId, "set-display-attr");
    // The slot's other keys and the sibling slots stay: a deep merge of the
    // one slot over the bag the row shows (its projection).
    return await this.write(
      rowId,
      RowWrites.mergeAttributes("deep", { children: { [preset]: { ...patch } } }, []),
      attrContext(rowId, [`children.${preset}`]),
    );
  }

  /**
   * **Local only** — merge `patch` into the attribute bag of `rowId` (per
   * top-level key; an `undefined` value is skipped) and notify, WITHOUT
   * persisting anything: a write entered in the row-field ledger and
   * confirmed at once, so every display copy shows it and each changed key
   * is announced once. A row this sheet does not hold is ignored. A write
   * that should persist goes through {@link setDisplayAttr}.
   */
  patchLocal(rowId: RowLocalId, patch: JsonObject): void {
    if (this.#rows.row(rowId) == null) return;
    const write = this.ledger.begin(
      rowId,
      RowWrites.mergeAttributes("shallow", patch, []),
      RowMetadataStore.ACTOR,
      SheetRowFields.writeOpts(),
    );
    this.ledger.accept(write);
  }

  /**
   * The row's own fields as this sheet holds them (the ledger's rows-side
   * display): its metadata — label, height, formula, lookup key, attribute
   * bag. `null` when the row is not this sheet's.
   */
  peek(rowId: RowLocalId): Option<RowWrites.Fields> {
    if (this.#rows.row(rowId) == null) return null;
    const meta = this.#stores.stores().rowMetadata.get(rowId);
    return {
      label: meta.label,
      heightPx: meta.heightPx,
      formula: meta.formula,
      lookupKey: meta.lookupKey,
      attributes: meta.attributes,
    };
  }

  /**
   * Show the ledger's `delta` on this sheet's copy of the row, SILENTLY (the
   * ledger announces each changed field once): the metadata record (the
   * attribute bag key by key, so a key only this copy holds stays), an
   * alias's instance props (their names answered, announced as
   * `props.<name>`), and — when the formula changed — the row rebound and
   * recomputed with its dependents over the observed range. A row this sheet
   * does not hold is ignored.
   */
  show(rowId: RowLocalId, delta: RowWrites.Delta): readonly string[] {
    const row = this.#rows.row(rowId);
    if (row == null) return [];
    const stores = this.#stores.stores();
    const before = stores.rowMetadata.get(rowId);
    const patch: { -readonly [K in keyof RowMetadata]?: RowMetadata[K] } = {};
    if (delta.set.label !== undefined) patch.label = delta.set.label ?? null;
    if (delta.set.heightPx !== undefined) patch.heightPx = delta.set.heightPx ?? null;
    if (delta.set.formula !== undefined) patch.formula = delta.set.formula ?? null;
    if (delta.set.lookupKey !== undefined) patch.lookupKey = delta.set.lookupKey ?? null;
    stores.rowMetadata.show(rowId, patch);
    const attrs = delta.attributes;
    if (attrs != null) {
      let next: Option<JsonObject> = null;
      if (attrs.after != null) {
        const bag: { [key: string]: Json } = { ...(before.attributes ?? {}) };
        for (const key of attrs.keys) {
          const value = attrs.after[key];
          if (value === undefined) delete bag[key];
          else bag[key] = value;
        }
        next = bag;
      }
      stores.rowMetadata.showAttributes(rowId, next, attrs.keys);
    }
    const extra: string[] = [];
    if (attrs != null && attrs.keys.includes("props") && row instanceof AliasRow) {
      const props = Json.child(attrs.after, "props") ?? {};
      for (const name of stores.aliasInstance.showProps(rowId, props)) extra.push(`props.${name}`);
    }
    if (patch.formula !== undefined && (patch.formula ?? null) !== (before.formula ?? null)) {
      this.#stores.formulaChanged(rowId, row);
    }
    return extra;
  }

  /**
   * Enter `change` of row `rowId` in the row-field ledger (painted at once,
   * announced as `RowMetadataStore.ACTOR`) and send it through the sheet's
   * backend; resolves `persisted: "backend"` on acceptance. A refusal (or no
   * answer) takes back this write's own change — never a newer one — and,
   * when a later PATCH carried it, PATCHes the surviving bag again; it
   * rejects with `RowWriteError` (step `"update"`, `context` leading its
   * details). An ephemeral (not-yet-promoted) sheet sends nothing and
   * resolves `deferred-to-promote`: the write is confirmed locally and the
   * value round-trips through serialize → hydrate on promote.
   */
  async write(rowId: RowLocalId, change: RowWrites.Change, context: string): Promise<SheetRendererCow.AttrWrite> {
    const write = this.ledger.begin(rowId, change, RowMetadataStore.ACTOR, SheetRowFields.writeOpts());
    if (this.#persist.isEphemeral()) {
      this.ledger.accept(write);
      return { persisted: "deferred-to-promote" };
    }
    await this.ledger.run(write, this.transport(rowId), context);
    return { persisted: "backend" };
  }

  /**
   * The one transport of the sheet's row-field writes: `Backend.updateRow`
   * of the row as it is keyed WHEN the body is sent (a promote re-keys it).
   */
  transport(rowId: RowLocalId): RowWrites.Transport {
    return this.#persist.transport(rowId);
  }

  /**
   * Enter, as an attribute write of `rowId`, what its wire bag now carries
   * of the store-held keys (`#WIRE_KEYS` — a fork's `hydrated`, a reset's
   * cleared `overrides`, a copy-on-write's `renderless`) that the bag the row
   * shows does not: painted at once into every display copy. The structural
   * op that changed those stores already announced the change (`hydrated`,
   * a reset's root invalidation, the render-less flag), so the write's
   * attribute keys are not announced again. Send it with `transport`
   * (the ledger's `run`).
   */
  beginWireAttributes(rowId: RowLocalId): RowWrites.Write {
    const row = this.requireRow(rowId, "update");
    const stores = this.#stores.stores();
    const snapshot: { [key: string]: Json } = { ...(this.#stores.snapshot(row).attributes ?? {}) };
    // The render-less flag is the renderless store's alone: a bag that
    // carried it before (shown there by an earlier write) must not keep it.
    if (stores.renderless.isRenderless(rowId)) snapshot.renderless = true;
    else delete snapshot.renderless;
    const wire: JsonObject = snapshot;
    const shown = this.ledger.project(rowId)?.attributes ?? stores.rowMetadata.get(rowId).attributes ?? {};
    const shownBag = Json.object(shown) ?? {};
    const set: { [key: string]: Json } = {};
    const unset: string[] = [];
    for (const key of SheetRowFields.#WIRE_KEYS) {
      if (Object.hasOwn(wire, key)) {
        const value = wire[key];
        if (value !== undefined && JSON.stringify(value) !== JSON.stringify(shownBag[key] ?? null)) set[key] = value;
      } else if (Object.hasOwn(shownBag, key)) {
        unset.push(key);
      }
    }
    return this.ledger.begin(rowId, RowWrites.mergeAttributes("shallow", set, unset), RowMetadataStore.ACTOR, {
      correction: RowMetadataStore.ACTOR,
      quietAttributes: true,
      capture: null,
    });
  }

  /**
   * The row `id` names, or an `SdkRequestError` (`outcome: "unaddressable"`,
   * nothing sent) for a display-attribute write aimed at a row this sheet
   * does not hold.
   */
  requireRow(id: RowLocalId, step: string): ModelRow {
    const row = this.#rows.row(id);
    if (row != null) return row;
    throw new SdkRequestError({
      boundary: "rows",
      step,
      method: "PATCH",
      path: "/api/rows/{rowId}",
      failure: { outcome: "unaddressable" },
      reason: "not_found",
      details: `row ${id.rawId} is not in sheet ${this.#rows.networkKey()}`,
    });
  }
}
